import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import type { CentrifugeProgressMessage } from '@/hooks/useCentrifugeProgress';
import SocketProgressBar from '@/app/common/components/SocketProgressBar';
import { getIndexerErrorInfo } from '../../api/errors';
import {
  useInspectUrlsMutation,
  useLazyGetProjectJobQuery,
  usePatchOnboardingMutation,
} from '../../api/indexerApi';
import type { Project, ProjectJob } from '../../api/types';
import { SitemapUrlImport } from '../SitemapUrlImport';
import { UrlListField } from '../UrlListField';
import { isJobDone } from '../../lib/job-labels';
import {
  ONBOARDING_LOW_URL_WARNING_THRESHOLD,
  ONBOARDING_MAX_INSPECT_URLS,
  ONBOARDING_MIN_INSPECT_URLS,
  ONBOARDING_RECOMMENDED_INSPECT_URLS,
} from '../../lib/onboarding-inspect-limits';
import {
  inspectCompleteFromJob,
  parseJobProgressCompletion,
  type OnboardingInspectCompletePayload,
} from '../../lib/onboarding-inspect-complete';
import { useProject } from '../../lib/project-context';
import {
  formatFaNumber,
  mergeUrlLists,
  parseUrlList,
  projectUrlHintsWithDomain,
  validateProjectUrlList,
} from '../../lib/utils';

const FETCH_FALLBACK_ATTEMPTS = 3;
const FETCH_FALLBACK_MS = 800;
const PROGRESS_DONE_MESSAGE =
  'بازرسی وضعیت ایندکس تمام شد. در حال رفتن به مرحله بعد…';
const PROGRESS_DONE_FALLBACK_MESSAGE =
  'بازرسی وضعیت ایندکس تمام شد. برای رفتن به مرحله بعد روی «ادامه» بزنید.';

export function OnboardingInspectStep({
  project,
  projectRef,
  inspectJobId,
  onComplete,
}: {
  project: Project;
  projectRef: string;
  inspectJobId: string | null;
  onComplete: (payload: OnboardingInspectCompletePayload) => void;
}) {
  const { domainName } = useProject();
  const projectHints = useMemo(
    () => projectUrlHintsWithDomain(project, domainName),
    [project, domainName],
  );
  const [urlText, setUrlText] = useState('');
  const [busy, setBusy] = useState(false);
  const [lowUrlWarningOpen, setLowUrlWarningOpen] = useState(false);
  const [message, setMessage] = useState<{
    variant: string;
    text: string;
  } | null>(null);
  const [job, setJob] = useState<ProjectJob | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [doneJobId, setDoneJobId] = useState<string | null>(null);
  const [progressDone, setProgressDone] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const completedRef = useRef(false);
  const completionMetaRef = useRef(
    null as ReturnType<typeof parseJobProgressCompletion>,
  );
  const [inspectUrls] = useInspectUrlsMutation();
  const [getProjectJob] = useLazyGetProjectJobQuery();
  const [patchOnboarding] = usePatchOnboardingMutation();

  const urls = useMemo(() => parseUrlList(urlText), [urlText]);
  const validationError = useMemo(
    () =>
      validateProjectUrlList(urls, projectHints, ONBOARDING_MAX_INSPECT_URLS),
    [urls, projectHints],
  );
  const hasMinUrls = urls.length >= ONBOARDING_MIN_INSPECT_URLS;
  const isLowUrlCount =
    urls.length > 0 && urls.length < ONBOARDING_LOW_URL_WARNING_THRESHOLD;

  const markProgressDone = useCallback((jobId: string) => {
    setDoneJobId(jobId);
    setProgressDone(true);
    setActiveJobId(null);
    setMessage({
      variant: 'success',
      text: PROGRESS_DONE_MESSAGE,
    });
  }, []);

  const resolveInspectPayload = useCallback(
    async (jobId: string): Promise<OnboardingInspectCompletePayload | null> => {
      for (let attempt = 0; attempt < FETCH_FALLBACK_ATTEMPTS; attempt++) {
        const loaded = await getProjectJob(
          { projectId: projectRef, jobId },
          true,
        ).unwrap();
        setJob(loaded);
        const fromJob = inspectCompleteFromJob(loaded);
        if (fromJob) return fromJob;
        if (attempt < FETCH_FALLBACK_ATTEMPTS - 1) {
          await new Promise(resolve => setTimeout(resolve, FETCH_FALLBACK_MS));
        }
      }
      return null;
    },
    [projectRef, getProjectJob],
  );

  const advanceToNext = useCallback(
    async (jobId: string) => {
      if (completedRef.current) return;
      setFinishing(true);
      try {
        const payload = await resolveInspectPayload(jobId);
        if (!payload) {
          setMessage({
            variant: 'success',
            text: PROGRESS_DONE_FALLBACK_MESSAGE,
          });
          return;
        }

        completedRef.current = true;
        await patchOnboarding({
          projectId: projectRef,
          inspectJobId: jobId,
          indexJobId: '',
          step: 4,
        }).unwrap();
        onComplete(payload);
      } catch (err) {
        completedRef.current = false;
        setMessage({
          variant: 'warning',
          text:
            getIndexerErrorInfo(err).message || PROGRESS_DONE_FALLBACK_MESSAGE,
        });
      } finally {
        setFinishing(false);
      }
    },
    [projectRef, onComplete, patchOnboarding, resolveInspectPayload],
  );

  const finishInspectJob = useCallback(
    async (jobId: string) => {
      markProgressDone(jobId);
      await advanceToNext(jobId);
    },
    [markProgressDone, advanceToNext],
  );

  const handleSocketProgress = useCallback(
    (data: CentrifugeProgressMessage) => {
      const meta = parseJobProgressCompletion(data);
      if (meta) {
        completionMetaRef.current = meta;
      }
    },
    [],
  );

  useEffect(() => {
    if (!inspectJobId || progressDone || completedRef.current) return;

    let cancelled = false;
    setActiveJobId(inspectJobId);

    void getProjectJob({ projectId: projectRef, jobId: inspectJobId }, true)
      .unwrap()
      .then(loaded => {
        if (cancelled || progressDone || completedRef.current) return;
        setJob(loaded);
        if (isJobDone(loaded.state) && (loaded.results?.length ?? 0) > 0) {
          const payload = inspectCompleteFromJob(loaded);
          if (payload) {
            completionMetaRef.current = {
              state: payload.state,
              jobKind: 'inspect',
              indexStatusSummary: payload.indexStatusSummary,
            };
            markProgressDone(inspectJobId);
          }
        }
      })
      .catch(() => {
        /* socket progress handles active jobs */
      });

    return () => {
      cancelled = true;
    };
  }, [
    inspectJobId,
    projectRef,
    getProjectJob,
    progressDone,
    markProgressDone,
  ]);

  function onImportFromSitemap(selected: string[]) {
    if (selected.length === 0) return;
    setUrlText(
      mergeUrlLists(urls, selected, ONBOARDING_MAX_INSPECT_URLS).join('\n'),
    );
    setLowUrlWarningOpen(false);
    setMessage({
      variant: 'success',
      text: `${formatFaNumber(selected.length)} آدرس از نقشه سایت اضافه شد.`,
    });
  }

  async function runInspect() {
    setBusy(true);
    setMessage(null);
    setLowUrlWarningOpen(false);
    setProgressDone(false);
    setDoneJobId(null);
    completedRef.current = false;
    completionMetaRef.current = null;
    try {
      const result = await inspectUrls({
        projectId: projectRef,
        urls,
      }).unwrap();
      const jobId = result.jobId;
      setMessage({
        variant: 'info',
        text: 'بازرسی وضعیت ایندکس شروع شد، حدود ۲ الی ۲۰ دقیقه ممکنه ارسال داده از سمت گوگل طول بشه، لطفاً صبر کنید…',
      });
      await patchOnboarding({
        projectId: projectRef,
        inspectJobId: jobId,
        step: 3,
      }).unwrap();
      setActiveJobId(jobId);
    } catch (err) {
      setMessage({
        variant: 'danger',
        text: getIndexerErrorInfo(err).message || 'ارسال بازرسی ناموفق بود',
      });
    } finally {
      setBusy(false);
    }
  }

  function onStartClick() {
    if (!hasMinUrls) {
      setMessage({
        variant: 'warning',
        text: 'حداقل یک آدرس وارد کنید.',
      });
      return;
    }
    if (validationError) {
      setMessage({ variant: 'danger', text: validationError });
      return;
    }
    if (isLowUrlCount) {
      setLowUrlWarningOpen(true);
      return;
    }
    void runInspect();
  }

  const continueJobId = doneJobId ?? activeJobId ?? job?.id ?? inspectJobId;
  const socketActive = Boolean(
    activeJobId && !progressDone && (!job || !isJobDone(job.state)),
  );

  return (
    <Card className="mb-3 border">
      <Card.Header className="bg-body-tertiary">
        <h2 className="h5 mb-0">بازرسی اولیه وضعیت ایندکس</h2>
      </Card.Header>
      <Card.Body>
        <p className="text-body mb-3 fs--1">
          حداقل یک آدرس از سایت را وارد کنید (دستی یا از sitemap). بلافاصله وضعیت
          ایندکس از گوگل گرفته می‌شود. برای بازخورد بهتر در نمای کلی،{' '}
          {formatFaNumber(ONBOARDING_RECOMMENDED_INSPECT_URLS)} آدرس پیشنهاد
          می‌شود (حداکثر {formatFaNumber(ONBOARDING_MAX_INSPECT_URLS)}).
        </p>

        {message && (
          <Alert variant={message.variant} className="py-2 small mb-3">
            {message.text}
          </Alert>
        )}

        {lowUrlWarningOpen && !job && !progressDone && (
          <Alert variant="warning" className="small mb-3">
            <div className="fw-semibold mb-1">آدرس‌ها کم است</div>
            <p className="mb-0">
              الان {formatFaNumber(urls.length)} آدرس دارید. برای دیدن بازخورد
              بهتر در نمای کلی، پیشنهاد می‌کنیم حدود{' '}
              {formatFaNumber(ONBOARDING_RECOMMENDED_INSPECT_URLS)} آدرس وارد کنید.
            </p>
          </Alert>
        )}

        {!job && !activeJobId && !progressDone && (
          <>
            <div className="d-flex flex-wrap gap-2 mb-2">
              <SitemapUrlImport
                projectId={projectRef}
                project={projectHints}
                limit={ONBOARDING_MAX_INSPECT_URLS}
                existingUrls={urls}
                onImport={onImportFromSitemap}
              />
              <span className="small text-secondary align-self-center">
                {formatFaNumber(urls.length)} آدرس · پیشنهاد{' '}
                {formatFaNumber(ONBOARDING_RECOMMENDED_INSPECT_URLS)} · حداکثر{' '}
                {formatFaNumber(ONBOARDING_MAX_INSPECT_URLS)}
              </span>
            </div>
            <UrlListField
              value={urlText}
              onChange={(next) => {
                setUrlText(next);
                setLowUrlWarningOpen(false);
              }}
              project={projectHints}
              limit={ONBOARDING_MAX_INSPECT_URLS}
            />
            {validationError && urls.length > 0 && (
              <div className="small text-danger mt-1">{validationError}</div>
            )}
          </>
        )}

        {(job || activeJobId || progressDone) && (
          <div>
            {activeJobId && !progressDone && (
              <SocketProgressBar
                channelId={activeJobId}
                active={socketActive}
                description="در حال بازرسی وضعیت ایندکس…"
                onProgress={handleSocketProgress}
                onComplete={() => void finishInspectJob(activeJobId)}
              />
            )}
            {finishing && (
              <div className="d-flex align-items-center gap-2 mt-2 small text-secondary">
                <Spinner animation="border" size="sm" />
                در حال رفتن به مرحله بعد…
              </div>
            )}
          </div>
        )}
      </Card.Body>
      {lowUrlWarningOpen && !job && !progressDone && (
        <Card.Footer className="bg-body-tertiary border-top d-flex flex-wrap gap-2 justify-content-end py-3">
          <Button
            variant="outline-warning"
            disabled={busy}
            onClick={() => setLowUrlWarningOpen(false)}
          >
            آدرس بیشتر اضافه می‌کنم
          </Button>
          <Button
            variant="warning"
            disabled={busy}
            onClick={() => void runInspect()}
          >
            {busy ? 'در حال ارسال…' : 'بیشتر ندارم — شروع بازرسی'}
          </Button>
        </Card.Footer>
      )}
      {!job && !activeJobId && !progressDone && !lowUrlWarningOpen && (
        <Card.Footer className="bg-body-tertiary border-top d-flex justify-content-end py-3">
          <Button
            variant="falcon-primary"
            disabled={busy || !hasMinUrls}
            onClick={() => onStartClick()}
          >
            {busy ? 'در حال ارسال…' : 'شروع بازرسی'}
          </Button>
        </Card.Footer>
      )}
      {progressDone && continueJobId && (
        <Card.Footer className="bg-body-tertiary border-top d-flex justify-content-end py-3">
          <Button
            variant="falcon-primary"
            disabled={finishing}
            onClick={() => void advanceToNext(continueJobId)}
          >
            {finishing ? 'در حال ادامه…' : 'ادامه'}
          </Button>
        </Card.Footer>
      )}
    </Card>
  );
}
